import React from 'react';
import { useSecurityStore } from '../../stores/SecurityStore'; // Make sure path is correct

export  const Risk = () => {
  // Get the raw data and compute derived values inside the component
  const { 
    repositories,
    loading,
    error,
    lastUpdated 
  } = useSecurityStore();

  // Compute aggregated counts directly in component
  const aggregated = React.useMemo(() => {
    return repositories.reduce((totals, repo) => ({
      totalFindings: totals.totalFindings + (repo.totalFindings || 0),
      verifiedFindings: totals.verifiedFindings + (repo.verifiedFindings || 0),
      unverifiedFindings: totals.unverifiedFindings + (repo.unverifiedFindings || 0),
      totalScans: totals.totalScans + (repo.totalScans || 0),
      totalRepositories: totals.totalRepositories + 1
    }), {
      totalFindings: 0,
      verifiedFindings: 0,
      unverifiedFindings: 0,
      totalScans: 0,
      totalRepositories: 0
    });
  }, [repositories]);

  // Compute summary stats
  const summary = React.useMemo(() => {
    const riskCounts = repositories.reduce((counts, repo) => {
      const riskLevel = repo.verifiedFindings > 10 ? 'Critical' :
                       repo.verifiedFindings > 5 ? 'High' :
                       repo.verifiedFindings > 0 ? 'Medium' : 'Secure';
      counts[riskLevel] = (counts[riskLevel] || 0) + 1;
      return counts;
    }, { Critical: 0, High: 0, Medium: 0, Secure: 0 });

    return {
      ...aggregated,
      repositoriesWithIssues: repositories.filter(repo => repo.verifiedFindings > 0).length,
      secureRepositories: repositories.filter(repo => repo.verifiedFindings === 0).length,
      processingRepositories: repositories.filter(repo => repo.status === 1 || repo.totalScans === 0).length,
      riskBreakdown: riskCounts,
      averageFindingsPerRepo: aggregated.totalRepositories > 0 ? 
        Math.round(aggregated.totalFindings / aggregated.totalRepositories * 100) / 100 : 0
    };
  }, [repositories, aggregated]);

  // Debug logging
  console.log('Repositories in Risk component:', repositories);
  console.log('Aggregated counts:', aggregated);
  console.log('Summary stats:', summary);

  if (loading) return <div>Loading Risk...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        <h1 className="text-black text-2xl md:text-3xl font-bold mb-4 md:mb-8">Security Risk Dashboard</h1>
      
      {lastUpdated && (
        <p className="text-sm text-[#8D82AA] mb-4">
          Last updated: {lastUpdated.toLocaleString()}
        </p>
      )}

      {/* Debug Info */}
      <div className="mb-4 p-2 bg-[#F4F0FD] border border-[#C0ACF3] rounded text-xs text-[#8D82AA]">
        <p>Repositories loaded: {repositories.length}</p>
        <p>Sample repository: {repositories[0] ? JSON.stringify(repositories[0], null, 2) : 'None'}</p>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[#C0ACF3] p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-[#8D82AA]">Total Findings</h3>
          <div className="text-2xl font-bold text-black">{aggregated.totalFindings}</div>
        </div>
        
        <div className="bg-white border border-[#C0ACF3] p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-[#8D82AA]">Verified Findings</h3>
          <div className="text-2xl font-bold text-black">{aggregated.verifiedFindings}</div>
        </div>
        
        <div className="bg-white border border-[#C0ACF3] p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-[#8D82AA]">Unverified Findings</h3>
          <div className="text-2xl font-bold text-black">{aggregated.unverifiedFindings}</div>
        </div>
        
        <div className="bg-white border border-[#C0ACF3] p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-[#8D82AA]">Total Repositories</h3>
          <div className="text-2xl font-bold text-black">{aggregated.totalRepositories}</div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#C0ACF3] p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 text-[#8D82AA]">Risk Breakdown</h3>
          <div className="space-y-1 text-black">
            <p>Critical: {summary.riskBreakdown?.Critical || 0}</p>
            <p>High: {summary.riskBreakdown?.High || 0}</p>
            <p>Medium: {summary.riskBreakdown?.Medium || 0}</p>
            <p>Secure: {summary.riskBreakdown?.Secure || 0}</p>
          </div>
        </div>
        
        <div className="bg-white border border-[#C0ACF3] p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 text-[#8D82AA]">Repository Status</h3>
          <div className="space-y-1 text-black">
            <p>With Issues: {summary.repositoriesWithIssues}</p>
            <p>Secure: {summary.secureRepositories}</p>
            <p>Processing: {summary.processingRepositories}</p>
            <p>Avg Findings/Repo: {summary.averageFindingsPerRepo}</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};


export default Risk

